import { PU40Report } from "@/components/verizon/PU40Report";
import { PulseCheckReport } from "@/components/verizon/PulseCheckReport";
import { AVBReport } from "@/components/verizon/AVBReport";
import { useCallback, useEffect, useState } from "react";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, RefreshCcw, CloudDownload } from "lucide-react";
import { getVzAdminSummary, vzSyncNow, getVzSnapshots } from "@/lib/verizonSalesApi";
import { VerizonSalesView, LastSyncBadge } from "@/components/verizon/VerizonSalesView";
import { XlsxUploader } from "@/components/verizon/XlsxUploader";
import { StoreMatching } from "@/components/verizon/StoreMatching";
import { PacingAlertsPanel } from "@/components/verizon/PacingAlertsPanel";
import { WeeklyDigestPanel } from "@/components/verizon/WeeklyDigestPanel";
import { GoalEmailsPanel } from "@/components/verizon/GoalEmailsPanel";
import { EmailExclusionsPanel } from "@/components/verizon/EmailExclusionsPanel";
import { StoreGoalsCard } from "@/components/verizon/StoreGoalsCard";
import { LeaderboardSettingsPanel } from "@/components/verizon/LeaderboardSettingsPanel";
import { OverallRankSettingsPanel } from "@/components/verizon/OverallRankSettingsPanel";
import { useAdminAccess } from "@/lib/adminAccess";

const TABS = [
  ["matching", "vz_matching", "Store Matching"], ["goals", "vz_goals", "Store Goals"],
  ["lb-settings", "vz_leaderboard", "Leaderboard Settings"], ["overall-settings", "vz_overall_rank", "Overall Rank Settings"],
  ["alerts", "vz_alerts", "Alerts & Digest"], ["setup", "vz_sync", "Sync Setup & Upload"],
];

const ViewOnlyNote = ({ area }) => (
  <div className="mb-4 px-4 py-2 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800" data-testid={`vz-view-only-${area}`}>
    View only: you can look at this tab, but saving, sending and uploading are turned off for your login.
  </div>
);

const SyncHistory = ({ snapshots }) => (
  <Card className="border-slate-200" data-testid="vz-sync-history">
    <CardHeader className="pb-2"><CardTitle className="text-base">Sync history</CardTitle></CardHeader>
    <CardContent className="space-y-1.5 text-xs">
      {snapshots.map((s) => (
        <div key={s.id} className="flex justify-between border-b border-slate-100 py-1.5">
          <span>{new Date(s.synced_at).toLocaleString()} · {s.source === "upload" ? "Upload" : "Google Drive"} · {s.file_name}{s.mode === "backfill" ? " · history backfill" : ""}</span>
          <span className="text-slate-500">{s.store_count} stores · through {s.updated_through || "—"}</span>
        </div>
      ))}
      {snapshots.length === 0 && <p className="text-slate-500">No syncs yet.</p>}
    </CardContent>
  </Card>
);

export default function AdminVerizonSales() {
  const [summary, setSummary] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState("dashboard");
  const { can } = useAdminAccess();
  const canSync = can("vz_sync");

  const load = useCallback(() => {
    getVzAdminSummary().then(setSummary).catch(() => toast.error("Failed to load Verizon sales"));
    if (canSync) getVzSnapshots().then(setSnapshots).catch(() => {});
  }, [canSync]);
  useEffect(load, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try { await vzSyncNow(); toast.success("Synced from Google Drive"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const hasData = summary && summary.stores.length > 0;
  return (
    <AdminLayout title="Dealer Sales">
      <div className="space-y-5" data-testid="admin-verizon-sales-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          {summary ? <LastSyncBadge lastSync={summary.last_sync} /> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="vz-refresh-btn"><RefreshCcw className="w-4 h-4 mr-1" /> Refresh</Button>
            {can("vz_sync", "manage") && <Button size="sm" onClick={syncNow} disabled={syncing} className="bg-teal hover:bg-teal/90" data-testid="vz-sync-now-btn">
              {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CloudDownload className="w-4 h-4 mr-1" />} Sync now
            </Button>}
          </div>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto justify-start" data-testid="vz-tabs">
            <TabsTrigger value="dashboard" data-testid="vz-tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="pu40" data-testid="vz-tab-pu40">Project Under 40</TabsTrigger>
            <TabsTrigger value="pulse" data-testid="vz-tab-pulse">Pulse Check</TabsTrigger>
            <TabsTrigger value="avb" data-testid="vz-tab-avb">Activation Volume Bonus</TabsTrigger>
            {TABS.filter(([, area]) => can(area)).map(([value, area, label]) => (
              <TabsTrigger key={value} value={value} data-testid={`vz-tab-${value}`}>
                {label}{value === "matching" && summary?.unmatched_count ? ` (${summary.unmatched_count})` : ""}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="dashboard" className="mt-5">
            {!summary ? <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-teal" /></div>
              : hasData ? <VerizonSalesView summary={summary} mode="admin" /> : (
                <Card className="border-dashed" data-testid="vz-empty-state"><CardContent className="py-12 text-center space-y-3">
                  <p className="text-slate-600">No Verizon sales data yet. Connect Google Drive or upload a DLAR file.</p>
                  {canSync && <Button onClick={() => setTab("setup")} data-testid="vz-goto-setup-btn">Set up sync</Button>}
                </CardContent></Card>
              )}
          </TabsContent>
          <TabsContent value="pu40" className="mt-5"><PU40Report mode="admin" canUpload={can("dealer_sales", "manage")} /></TabsContent>
          <TabsContent value="pulse" className="mt-5"><PulseCheckReport mode="admin" canUpload={can("dealer_sales", "manage")} /></TabsContent>
          <TabsContent value="avb" className="mt-5"><AVBReport mode="admin" canUpload={can("dealer_sales", "manage")} /></TabsContent>
          {can("vz_matching") && <TabsContent value="matching" className="mt-5">{!can("vz_matching", "manage") && <ViewOnlyNote area="vz_matching" />}<fieldset disabled={!can("vz_matching", "manage")} className="space-y-6 min-w-0">{summary && <StoreMatching stores={summary.stores} onChanged={load} />}</fieldset></TabsContent>}
          {can("vz_goals") && <TabsContent value="goals" className="mt-5">{!can("vz_goals", "manage") && <ViewOnlyNote area="vz_goals" />}<fieldset disabled={!can("vz_goals", "manage")} className="space-y-6 min-w-0"><StoreGoalsCard mode="admin" onChanged={load} /></fieldset></TabsContent>}
          {can("vz_leaderboard") && <TabsContent value="lb-settings" className="mt-5">{!can("vz_leaderboard", "manage") && <ViewOnlyNote area="vz_leaderboard" />}<fieldset disabled={!can("vz_leaderboard", "manage")} className="space-y-6 min-w-0"><LeaderboardSettingsPanel onSaved={load} /></fieldset></TabsContent>}
          {can("vz_overall_rank") && <TabsContent value="overall-settings" className="mt-5">{!can("vz_overall_rank", "manage") && <ViewOnlyNote area="vz_overall_rank" />}<fieldset disabled={!can("vz_overall_rank", "manage")} className="space-y-6 min-w-0"><OverallRankSettingsPanel onSaved={load} /></fieldset></TabsContent>}
          {can("vz_alerts") && <TabsContent value="alerts" className="mt-5 space-y-6">{!can("vz_alerts", "manage") && <ViewOnlyNote area="vz_alerts" />}<fieldset disabled={!can("vz_alerts", "manage")} className="space-y-6 min-w-0"><WeeklyDigestPanel /><GoalEmailsPanel /><PacingAlertsPanel /><EmailExclusionsPanel /></fieldset></TabsContent>}
          {can("vz_sync") && <TabsContent value="setup" className="mt-5 space-y-6">{!can("vz_sync", "manage") && <ViewOnlyNote area="vz_sync" />}
            {can("vz_sync", "manage") && <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-base">Manual upload (backup)</CardTitle></CardHeader>
              <CardContent><XlsxUploader onDone={load} /></CardContent></Card>}
            <SyncHistory snapshots={snapshots} />
          </TabsContent>}
        </Tabs>
      </div>
    </AdminLayout>
  );
}
